import { UserOutlined } from "@ant-design/icons";
import { Avatar, Flex, Progress, Rate } from "antd";
import type {
  CourseReview,
  IPublicCourseResponse,
} from "../../../app/features/courses/courses.types";
import IMAGES from "../../../assets/images";
import { enToBnNumber } from "../../../utils/number.utils";
import { formatDate } from "../../../utils/time.utils";
import ReviewLoading from "./ReviewLoading";

type Props = {
  course?: IPublicCourseResponse;
  isLoading?: boolean;
};
const CourseReviews = ({ course, isLoading }: Props) => {
  const ratingDistribution = [
    { stars: 5, ...ratingCountWithPercentage(course?.CourseReview || [], 5) },
    { stars: 4, ...ratingCountWithPercentage(course?.CourseReview || [], 4) },
    { stars: 3, ...ratingCountWithPercentage(course?.CourseReview || [], 3) },
    { stars: 2, ...ratingCountWithPercentage(course?.CourseReview || [], 2) },
    { stars: 1, ...ratingCountWithPercentage(course?.CourseReview || [], 1) },
  ];

  if (isLoading) {
    return <ReviewLoading />;
  }

  return (
    <div className="bg-white p-6 border border-theme-zinc-200 rounded-lg">
      {/* Header Section */}
      <div className="mb-6">
        <h2 className="text-theme-zinc-700 text-2xl font-medium leading-9">
          এই কোর্স সম্পর্কে শিক্ষার্থীদের মতামত
        </h2>
        <p className="text-theme-slate-500 text-base font-normal leading-7">
          {enToBnNumber(course?.ratingCount || 0)} জন তাদের নিজেদের মতামত প্রদান
          করেছেন
        </p>
      </div>

      {/* Rating Overview */}
      <div className="mb-6 pb-6 border-b border-gray-200">
        <div className="md:flex items-start gap-36 space-y-6 md:space-y-0">
          {/* Overall Rating */}
          <Flex align="center" className="flex-col" wrap>
            <div className="text-theme-zinc-700 text-6xl md:text-4xl font-medium">
              {enToBnNumber(Number(course?.ratingAvg || 0))}
              <span className="text-xl md:text-base">/৫</span>
            </div>
            <Rate
              disabled
              value={Number(course?.ratingAvg || 0)}
              allowHalf
              className="custom-rate"
            />
          </Flex>

          {/* Rating Bars */}
          <div className="flex-1">
            {ratingDistribution.map((item) => (
              <Flex align="center" gap={20} key={item.stars} className="mb-2">
                <Rate
                  disabled
                  defaultValue={item.stars}
                  className="custom-rate"
                />
                <Progress
                  percent={item.percentage}
                  showInfo={false}
                  strokeColor="#FC8415"
                  trailColor="#E9EFF6"
                  className="flex-1"
                  strokeLinecap="butt"
                />
                <span className="text-theme-slate-600 text-lg font-normal w-8">
                  {enToBnNumber(item.count)}
                </span>
              </Flex>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-3">
        {course?.CourseReview?.map((review, index) => (
          <div
            key={index}
            className="pb-3 border-b border-theme-zinc-200 last:border-0"
          >
            <Flex gap={16} wrap>
              <Avatar
                size={48}
                src={review?.userAvatar || IMAGES.avatar}
                icon={<UserOutlined />}
              />
              <div className="flex-1">
                <Flex align="center" wrap className="mb-1 md:gap-5 gap-1">
                  <h3 className="text-theme-slate-700 text-lg font-normal">
                    {review?.userName || review?.userEmail || ""}
                  </h3>
                  <Rate
                    disabled
                    defaultValue={review?.rating || 0}
                    className="custom-rate"
                  />
                </Flex>
                <p className="text-theme-slate-500 text-sm font-normal">
                  {formatDate(review?.createdAt)}
                </p>
              </div>
            </Flex>
            <p className="text-theme-slate-600 text-sm font-normal mt-3">
              {review?.comment}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CourseReviews;

function ratingCountWithPercentage(
  reviews: CourseReview[],
  star: number
): { count: number; percentage: number } {
  const totalReviews = reviews.length;

  if (totalReviews === 0) {
    return { count: 0, percentage: 0 };
  }

  const count = reviews.filter((r) => r.rating === star).length;

  const percentage = Number(((count / totalReviews) * 100).toFixed(2));

  return { count, percentage };
}
